import type { CSSProperties, KeyboardEvent } from 'react'
import { Fragment, memo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation } from 'react-router'

import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import {
  findGroupByPathname,
  getGroupLandingPath,
} from '~/components/layouts/MainLayout/menuGroups'
import { isEnterKey } from '~/libs/keyboard'

/**
 * パンくずの 1 項目。
 *
 * 先頭の項目は、`to` / `onClick` / `bold` / `noLink` のどれも無く、末尾でもなく、現在の pathname が
 * サイドメニューのグループに属する時、そのグループの先頭画面へのリンクになる (#10126 の Des 判断)。
 * 中間の項目は自動ではリンクにならない。
 */
export type PageBreadcrumbItem = {
  label: string
  /** 内部遷移先 path。指定すると `RouterLink` で描画され、cmd/ctrl+クリックで別タブが開ける。 */
  to?: string
  /** `to` が無い場合のクリックハンドラ (非推奨。遷移先があるなら `to` を使う)。 */
  onClick?: () => void
  /** 現在地を表す末尾クラム。 */
  bold?: boolean
  /**
   * リンクにせず文字だけにする (`to` / `onClick` / 先頭のグループの自動リンクのどれも付けない)。
   * 保存中など、パンくずから画面を離れさせたくない時に使う (#7007)。
   */
  noLink?: boolean
}

type Props = {
  items: PageBreadcrumbItem[]
}

const baseItemStyle: CSSProperties = {
  color: LightModeSemanticColors.Text.highEmphasis.value,
  textDecoration: 'none',
  fontSize: fontSizes.md.value,
  fontWeight: 300,
}

const currentStyle: CSSProperties = {
  color: LightModeSemanticColors.Text.highEmphasis.value,
  fontSize: fontSizes.xl.value,
  fontWeight: 700,
}

const separatorStyle: CSSProperties = {
  color: LightModeSemanticColors.Text.lowEmphasis.value,
  fontSize: fontSizes.md.value,
  fontWeight: 300,
  userSelect: 'none',
}

const ClickableCrumb = memo(
  ({
    label,
    onClick,
    to,
  }: {
    label: string
    onClick?: () => void
    to?: string
  }): React.ReactElement => {
    const [hover, setHover] = useState(false)

    const style: CSSProperties = {
      ...baseItemStyle,
      textDecoration: hover ? 'underline' : 'none',
    }

    if (to !== undefined) {
      return (
        <RouterLink
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={style}
          to={to}
        >
          {label}
        </RouterLink>
      )
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>): void => {
      if (isEnterKey(e) && onClick !== undefined) {
        e.preventDefault()
        onClick()
      }
    }

    return (
      <span
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        role="link"
        style={{ ...style, cursor: 'pointer' }}
        tabIndex={0}
      >
        {label}
      </span>
    )
  },
)
ClickableCrumb.displayName = 'ClickableCrumb'

export const PageBreadcrumb = ({ items }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const group = findGroupByPathname(pathname)
  return (
    <nav
      aria-label={t('common:breadcrumb.ariaLabel')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: fontSizes.md.value,
      }}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1
        // #10126 の Des 判断: 先頭のメニューグループ名は先頭画面へのリンク。
        // 所属はサイドメニューと共通にし、明示された導線と現在地は維持する。
        const to =
          item.noLink === true
            ? undefined
            : (item.to ??
              (i === 0 &&
              !isLast &&
              item.onClick === undefined &&
              group !== undefined
                ? getGroupLandingPath(group)
                : undefined))
        const onClick = item.noLink === true ? undefined : item.onClick
        return (
          <Fragment key={i}>
            {item.bold === true ? (
              <span aria-current="page" style={currentStyle}>
                {item.label}
              </span>
            ) : to !== undefined || onClick !== undefined ? (
              <ClickableCrumb label={item.label} onClick={onClick} to={to} />
            ) : (
              <span style={baseItemStyle}>{item.label}</span>
            )}
            {!isLast && (
              <span aria-hidden="true" style={separatorStyle}>
                /
              </span>
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
